import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Map, FileImage, FileText, Globe, 
  Send, ClipboardCheck, Package, ArrowRight 
} from 'lucide-react';
import { RippleButton } from '../components/effects/RippleButton';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const layersData = [
  "Contours – Topography / Network / Bounds",
  "Slope – Morphometry / Hillshade / Aspect",
  "Characteristics – Erosion / Flood Risk",
  "Flow Rate / Direction / Accumulation",
  "Elevation / Landforms / Micro-peaks",
  "Sub-basins / Drainage / Valleys",
  "Sun / Wind exposure / View Shed"
];

const formatsData = [
  { format: "PNG, TIF, JPEG", type: "Image", icon: FileImage },
  { format: "PDF", type: "Document", icon: FileText },
  { format: "KMZ", type: "Google Earth", icon: Globe }
];

const processSteps = [
  {
    step: 1,
    title: "Submit Your Site",
    description: "Provide your area of interest in kml/kmz/dwg and request a quote.",
    icon: Send
  },
  {
    step: 2,
    title: "Process Your Request",
    description: "Our team reviews your requirements and provides an estimate.",
    icon: ClipboardCheck
  },
  {
    step: 3,
    title: "Receive Your Maps",
    description: "After processing the payment, we create a package for the AOI within a week. You will be notified when they are ready and will be provided with a folder in the cloud containing all outputs.",
    icon: Package
  }
];

// Reference image from user's upload
const MAPPING_REFERENCE_IMAGE = "https://customer-assets.emergentagent.com/job_38ca16b8-b881-4255-a6f4-b41a6f37c1b3/artifacts/ntuuhj1k_WhatsApp%20Image%202026-03-06%20at%202.27.27%E2%80%AFPM.jpeg";

// South India imagery for mapping sections - optimized
const SOUTH_INDIA_IMAGES = {
  hero: "https://images.unsplash.com/photo-1582870495095-bfe299c87a3b?w=1600&q=75&fm=webp",
  terrain: "https://images.unsplash.com/photo-1648035376406-233db3029aee?w=800&q=75&fm=webp",
  plantation: "https://images.unsplash.com/photo-1658051161493-1d311c4c7b4d?w=800&q=75&fm=webp",
};

export default function MappingServices() {
  return (
    <main className="min-h-screen bg-cream" data-testid="mapping-services-page">
      {/* Hero Section with Background Image */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src={SOUTH_INDIA_IMAGES.hero}
            alt="Western Ghats landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/95 via-cream/85 to-cream" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-earth text-sm tracking-[0.2em] uppercase mb-4"
            >
              Regenerative design and planning
            </motion.p>
            
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl lg:text-6xl text-forest mb-6"
              data-testid="mapping-headline"
            >
              Mapping Services
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-foreground/70 text-lg md:text-xl mb-8"
            >
              Have you been searching for detailed maps to get you going with ease?
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="bg-white rounded-2xl p-6 md:p-8 shadow-soft border border-border/30 inline-block"
            >
              <p className="text-forest font-serif text-xl md:text-2xl mb-2">
                Rs. 3,300 / sq km
              </p>
              <p className="text-foreground/60 text-sm md:text-base">
                Base maps with geographic and hydrologic layers visualized
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Maps Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-forest mb-12 text-center"
            >
              Why Maps?
            </motion.h2>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Basic */}
              <motion.div 
                variants={fadeInUp}
                className="bg-white rounded-2xl p-8 shadow-soft border border-border/30"
              >
                <span className="inline-block px-4 py-1 bg-moss/20 text-forest text-sm font-semibold rounded-full mb-4">
                  Basic
                </span>
                <p className="text-foreground/70 leading-relaxed">
                  Plot terrain to show landscape features, assess vegetation and the way water moves through it.
                </p>
              </motion.div>
              
              {/* Pro */}
              <motion.div 
                variants={fadeInUp}
                className="bg-forest rounded-2xl p-8 shadow-soft"
              >
                <span className="inline-block px-4 py-1 bg-white/20 text-white text-sm font-semibold rounded-full mb-4">
                  Pro
                </span>
                <p className="text-white/90 leading-relaxed">
                  Design and construct roads, buildings, earthen dams and detailed farm/restoration plans.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Landscape Imagery Section */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 gap-6"
          >
            <motion.div 
              variants={fadeInUp}
              className="relative overflow-hidden rounded-2xl h-64 md:h-80"
            >
              <img 
                src={SOUTH_INDIA_IMAGES.terrain}
                alt="Western Ghats terrain for topographic mapping"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="font-serif text-lg">Terrain Analysis</p>
                <p className="text-white/70 text-sm">Understanding contours & elevation</p>
              </div>
            </motion.div>
            
            <motion.div 
              variants={fadeInUp}
              className="relative overflow-hidden rounded-2xl h-64 md:h-80"
            >
              <img 
                src={SOUTH_INDIA_IMAGES.plantation}
                alt="Karnataka plantation landscape for watershed mapping"
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="font-serif text-lg">Watershed Planning</p>
                <p className="text-white/70 text-sm">Water flow & catchment analysis</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Layers / Outputs Section */}
      <section className="py-16 md:py-24 bg-sand/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-forest mb-4 text-center"
            >
              Layers / Outputs
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-foreground/60 text-center mb-12 max-w-2xl mx-auto"
            >
              Comprehensive geographic and hydrologic analysis layers
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto"
            >
              {layersData.map((layer, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-border/20"
                >
                  <div className="w-2 h-2 rounded-full bg-earth flex-shrink-0" />
                  <span className="text-foreground/80 text-sm">{layer}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Formats Section */}
      <section className="py-16 md:py-24 bg-forest text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl mb-12"
            >
              Output Formats
            </motion.h2>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-wrap justify-center gap-6"
            >
              {formatsData.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 min-w-[180px] border border-white/20"
                  >
                    <Icon className="w-8 h-8 mb-4 mx-auto text-white/80" />
                    <p className="font-semibold mb-1">{item.format}</p>
                    <p className="text-white/60 text-sm">{item.type}</p>
                  </div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-forest mb-16 text-center"
            >
              How It Works
            </motion.h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <motion.div 
                    key={step.step}
                    variants={fadeInUp}
                    className="relative"
                  >
                    {/* Connector line */}
                    {index < processSteps.length - 1 && (
                      <div className="hidden md:block absolute top-12 left-[60%] w-full h-0.5 bg-border/30" />
                    )}
                    
                    <div className="bg-white rounded-2xl p-8 shadow-soft border border-border/30 relative z-10">
                      <div className="w-16 h-16 rounded-full bg-forest/10 flex items-center justify-center mb-6">
                        <Icon className="w-8 h-8 text-forest" />
                      </div>
                      
                      <div className="absolute -top-4 -left-4 w-10 h-10 rounded-full bg-earth text-white flex items-center justify-center font-bold text-lg">
                        {step.step}
                      </div>
                      
                      <h3 className="font-serif text-xl text-forest mb-3">
                        {step.title}
                      </h3>
                      <p className="text-foreground/70 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-sand/30">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl text-forest mb-6"
            >
              Ready to Map Your Land?
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-foreground/70 text-lg mb-8 max-w-2xl mx-auto"
            >
              Get started with professional GIS mapping services. Submit your area of interest and receive detailed terrain analysis within a week.
            </motion.p>
            
            <motion.div variants={fadeInUp}>
              <Link to="/contact">
                <RippleButton className="btn-primary" data-testid="mapping-cta">
                  Request a Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </RippleButton>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
